import { PortalDialogProps } from "@app/services/modal.service";
import Modal from "../modal/modal.component";
import Button from "../button";
import { FormControl } from "../form-control";
import Input from "../input";
import { Form, Formik, FormikContextType } from "formik";
import { Images } from "@assets/images";
import { ChangeEvent, createRef, useEffect, useRef } from "react";
import { MAX_SIZE_UPLOAD, SystemMessage } from "@app/constants";
import { SettingUi, UpdateSettingUiForm } from "@app/types/setting-ui.type";
import Checkbox from "../checkbox";

interface SettingUiModalProps {
  isEdit: boolean;

  settingUi: SettingUi;
}

function SettingUiModal({
  portalDialogRef,
  portalData,
}: PortalDialogProps<SettingUiModalProps>) {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const formRef = createRef<FormikContextType<UpdateSettingUiForm>>();

  const onSubmit = (values: UpdateSettingUiForm) => {
    if (!formRef.current) return;

    if (
      !formRef.current.values.hasFileChange &&
      portalData?.settingUi?.bgImage
    ) {
      const settingUi = {
        bgColor: values.bgColor,
        isHeader: values.isHeader,
      };

      portalDialogRef.close({ settingUi });

      return;
    }

    const file = inputFileRef.current?.files?.[0] ?? null;

    if (file && file.size > MAX_SIZE_UPLOAD) {
      formRef.current.setFieldError("filename", SystemMessage.MAX_SIZE_UPLOAD);

      return;
    }

    const settingUi = {
      bgColor: values.bgColor,
      isHeader: values.isHeader,
      bgImage: file,
      hasFileChange: true,
    };

    portalDialogRef.close({ settingUi });
  };

  const onAddFileClick = () => {
    if (inputFileRef.current) {
      inputFileRef.current.click();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!formRef.current) return;

    if (!event.target.files?.length) {
      formRef.current.setFieldError("filename", SystemMessage.INPUT_REQUIRED);
      formRef.current.setFieldValue("filename", "");
      formRef.current.submitForm();

      return;
    }

    const file = event.target.files[0];

    if (file.size > MAX_SIZE_UPLOAD) {
      formRef.current.setFieldError("filename", SystemMessage.MAX_SIZE_UPLOAD);
      formRef.current.setFieldValue("filename", "");
      formRef.current.submitForm();

      return;
    }

    formRef.current.setFieldError("filename", undefined);
    formRef.current.setFieldValue("filename", file.name);
    formRef.current.setFieldValue("hasFileChange", true);
  };

  useEffect(() => {
    if (portalData?.settingUi && formRef.current) {
      formRef.current.setValues({
        bgColor: portalData.settingUi.bgColor ?? "",
        isHeader: portalData.settingUi.isHeader,
        filename: portalData.settingUi.bgImage ?? "",
      });
    }
  }, [portalData?.settingUi]);

  return (
    <Modal onCancel={portalDialogRef.close}>
      <div className="w-[423px] py-7 px-6">
        <div className="text-black font-semibold text-xl">
          {portalData?.isEdit ? "Chỉnh sửa" : "Thêm setting UI"}
        </div>
        <Formik
          innerRef={formRef}
          initialValues={{
            bgColor: "",
            isHeader: false,
            filename: "",
          }}
          onSubmit={onSubmit}
        >
          <Form>
            <FormControl name="bgColor">
              <Input className="mt-7" placeholder="Mã màu nền" />
            </FormControl>
            <FormControl name="filename">
              <Input
                className="mt-3"
                inputClassName="!pl-4 pr-[50px]"
                placeholder="Hình nền"
                disabled
                inputIcon={Images.LinkIcon.default}
                inputIconClassName="!right-5 !top-[14px] !left-auto !w-6 !h-6 cursor-pointer hover:bg-gray-300"
                onIconClick={onAddFileClick}
              />
            </FormControl>
            <input
              ref={inputFileRef}
              type="file"
              name="file"
              hidden
              onChange={handleFileChange}
            />
            <label htmlFor="">
              <div className="mt-6 flex">
                <FormControl name="isHeader">
                  <Checkbox
                    label="Là header"
                    labelClassName="text-sm text-[#83898C]"
                  />
                </FormControl>
              </div>
            </label>
            <div className="mt-11 flex justify-center gap-4">
              <Button
                size="xs"
                label="Hủy"
                theme="secondary"
                width={180}
                className="!h-[51px] !leading-[51px]"
                labelClassName="text-xl"
                onClick={portalDialogRef.close}
              />
              <Button
                type="submit"
                size="xs"
                label={portalData?.isEdit ? "Lưu" : "Thêm"}
                width={180}
                className="!h-[51px] !leading-[51px]"
                labelClassName="text-xl"
              />
            </div>
          </Form>
        </Formik>
      </div>
    </Modal>
  );
}

export default SettingUiModal;
